'use client';

import { type ReactNode, createContext, useContext, useState } from 'react';

type AuthModalContextType = {
  isOpen: boolean;
  openLoginModal: (redirectUrl?: string) => void;
  openSignupModal: (redirectUrl?: string) => void;
  closeModal: () => void;
  activeTab: 'login' | 'signup';
  redirectUrl: string | null;
};

const AuthModalContext = createContext<AuthModalContextType | undefined>(
  undefined
);

export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  const openLoginModal = (url?: string) => {
    setActiveTab('login');
    setRedirectUrl(url ?? null);
    setIsOpen(true);
  };

  const openSignupModal = (url?: string) => {
    setActiveTab('signup');
    setRedirectUrl(url ?? null);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setRedirectUrl(null);
  };

  return (
    <AuthModalContext.Provider
      value={{
        isOpen,
        openLoginModal,
        openSignupModal,
        closeModal,
        activeTab,
        redirectUrl,
      }}
    >
      {children}
    </AuthModalContext.Provider>
  );
}

export function useAuthModal() {
  const context = useContext(AuthModalContext);
  if (context === undefined) {
    throw new Error('useAuthModal must be used within an AuthModalProvider');
  }
  return context;
}
